'use client';

import React, { ReactElement } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  AnalyticsModuleTrackingProvider,
  AnalyticsModuleTrackingAttributes,
  useAnalyticsModuleTracking,
} from './AnalyticsModuleTracking';
import { useModuleViewedReport } from './hooks/useModuleViewedReport';
import { getModuleCTAType } from './utils';

interface TrackableModuleProps {
  children: ReactElement;
  initialAttributes?: Partial<AnalyticsModuleTrackingAttributes>;
  /**
   * Space-separated edges ("top", "bottom", or "top bottom") whose background should extend to
   * back an adjacent rounded-corner module. Rendered as `data-rc-extend` on the wrapper
   */
  rcExtend?: string;
  /** Whether the tracking should fire only once when the element enters the viewport */
  triggerOnce?: boolean;
  /**
   * Margin around the root element when calculating intersections
   * @see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/rootMargin
   */
  rootMargin?: string;
  /**
   * Threshold of visibility required to trigger the tracking
   * @see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/threshold
   */
  threshold?: number;
  /**
   * Whether this wrapper's own viewport observer reports the impression. Set it
   * false only for a module this wrapper cannot measure: a `position: fixed` root
   * leaves the wrapper with no area, and a zero-area target counts as fully visible
   * the moment it crosses the viewport, so the observer would report a module the
   * visitor cannot see. Such a module reports its own impression instead.
   */
  trackViewed?: boolean;
}

interface TrackableModuleWrapperProps {
  children: ReactElement;
  rcExtend?: string;
  triggerOnce?: boolean;
  rootMargin?: string;
  threshold?: number;
  trackViewed?: boolean;
}

/**
 * Wraps a component to track when it becomes visible in the viewport
 * Uses IntersectionObserver to detect visibility and triggers analytics events
 *
 * @param props - Component props
 * @returns The provided child element with tracking capabilities
 */
const TrackableModuleWrapper = ({
  children,
  rcExtend,
  triggerOnce = true,
  rootMargin = '-100px 0',
  threshold = 0,
  trackViewed = true,
}: TrackableModuleWrapperProps) => {
  const { attributes } = useAnalyticsModuleTracking();
  const reportViewed = useModuleViewedReport();
  const elementRef = React.useRef<HTMLElement>(null);

  // Set up the intersection observer for tracking when the module is in view
  const { ref: inViewRef } = useInView({
    triggerOnce,
    rootMargin,
    threshold,
    // Skipped rather than rejected inside `onChange`, because `triggerOnce`
    // unobserves on the first intersection - refusing the callback would spend the
    // impression rather than leave it to the module.
    skip: !trackViewed,
    onChange: inView => {
      if (inView) {
        reportViewed(elementRef.current);
      }
    },
  });

  // Combined ref callback that sets both refs
  const setRefs = React.useCallback(
    (node: HTMLElement | null) => {
      elementRef.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  return (
    <div ref={setRefs} data-module-index={attributes.moduleIndex} data-rc-extend={rcExtend}>
      {children}
    </div>
  );
};

/**
 * A component that tracks when a UI module becomes visible in the viewport
 * and sends analytics events with module metadata.
 *
 * Combines the AnalyticsModuleTrackingProvider for setting module attributes
 * with visibility tracking via IntersectionObserver.
 *
 * @example
 * ```tsx
 * <TrackableModule
 *   initialAttributes={{
 *     moduleTitle: 'Hero Banner',
 *     moduleType: 'hero',
 *     moduleIndex: '1',
 *     moduleLocation: 'https://www.example.com/hero'
 *   }}
 *   threshold={0.5}
 * >
 *   <HeroComponent />
 * </TrackableModule>
 * ```
 *
 * @param props - Component props
 * @returns The wrapped component with tracking capabilities
 */
const TrackableModule = ({
  children,
  initialAttributes,
  rcExtend,
  triggerOnce = true,
  rootMargin = '',
  threshold = 0.75,
  trackViewed = true,
}: TrackableModuleProps) => {
  return (
    <AnalyticsModuleTrackingProvider initialAttributes={initialAttributes}>
      <TrackableModuleWrapper
        rcExtend={rcExtend}
        triggerOnce={triggerOnce}
        rootMargin={rootMargin}
        threshold={threshold}
        trackViewed={trackViewed}>
        {children}
      </TrackableModuleWrapper>
    </AnalyticsModuleTrackingProvider>
  );
};

export { getModuleCTAType };
export default TrackableModule;
